import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { SettingsTabsEnum } from '@/Config/app/router/index.path';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { SubscriptionLoader } from '@/UI/Subscription/Subscription.loader';
import { UserProfilePageLoader } from '@/UI/User/UserProfile/UserPage.loader';
import AccountSettingsPage, {
  LoadingAccountSettingsPage,
} from '@/UI/User/UserSettings/AccountSettings';

const VALID_TABS = new Set<string>(Object.values(SettingsTabsEnum));

export const SettingsPage = () => {
  const { t } = useTranslation('settings_page');
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });
  // The subscription endpoint enforces `min_valid_role=SUPPORT`, so viewer
  // users get a 403. Track the error so the loading guard below can proceed
  // with `tier === undefined` instead of sitting on a spinner forever — the
  // Billing tab is already role-gated for those users, so missing tier info
  // is harmless.
  const { data: subscriptionData, error: subscriptionError } = useGetSubscription();
  const subscriptionSettled = !!subscriptionData || !!subscriptionError;

  // Tabs are always rendered. Disabled state and tooltip text depend on
  // role (orgadmin/admin/quickchat for both Org and Billing) and, for
  // Billing, also on whether billing is managed externally (custom tier or
  // Sony scenario). Backend endpoints enforce the same rules so a user can't
  // bypass the disabled UI by hitting the API directly.
  const permissions = aiAssistants?.current_ai_assistant?.permissions;
  const tier = subscriptionData?.current_plan.tier;

  const orgRoleAllowed = !!permissions?.account_settings;
  const billingRoleAllowed = !!permissions?.subscription;
  // Custom-tier accounts (Sony, enterprise contracts, etc.) are billed
  // off-platform — disable the in-app billing flow for them. This mirrors
  // the backend `is_billing_managed_externally` check.
  const billingExternallyManaged = tier === TierEnum.CUSTOM;

  const orgDisabled = !orgRoleAllowed;
  const billingDisabled = !billingRoleAllowed || billingExternallyManaged;

  // Pick tooltip per reason: role-blocked tabs share one message, the
  // tier/Sony case gets a distinct one because the user can't fix it via
  // role escalation.
  const orgTooltip = orgDisabled ? t('tooltips.role_disabled') : undefined;
  const billingTooltip = !billingRoleAllowed
    ? t('tooltips.role_disabled')
    : billingExternallyManaged
      ? t('tooltips.billing_managed_externally')
      : undefined;

  // Wait for both queries to settle before deciding the tab. A subscription
  // error (viewer hitting the SUPPORT-floor endpoint) counts as "settled" so
  // the page can still render the Account tab.
  if (!aiAssistants || !subscriptionSettled) {
    return (
      <div
        className="max-w-7xl w-full mx-auto flex items-center justify-center py-20"
        data-testid="settings-page-loading"
      >
        <Spinner />
      </div>
    );
  }

  const tabParam = searchParams.get('tab');
  const requestedTab =
    tabParam && VALID_TABS.has(tabParam)
      ? (tabParam as SettingsTabsEnum)
      : SettingsTabsEnum.Account;
  const isAccessible =
    requestedTab === SettingsTabsEnum.Account ||
    (requestedTab === SettingsTabsEnum.Organization && !orgDisabled) ||
    (requestedTab === SettingsTabsEnum.Billing && !billingDisabled);

  // Inaccessible deep link → redirect synchronously before any tab renders.
  // Doing this in render (vs. a post-mount useEffect) avoids the flash where
  // the requested-but-gated tab would briefly look selected.
  if (!isAccessible) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', SettingsTabsEnum.Account);
    return <Navigate to={{ search: next.toString() }} replace />;
  }

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    });
  };

  return (
    <div className="max-w-7xl w-full mx-auto">
      <h1 className="text-lg font-semibold text-foreground mb-6">{t('title')}</h1>
      <Tabs value={requestedTab} onValueChange={handleTabChange} id="tabs-settings">
        <TabsList className="mb-6" id="tabs-list">
          <TabsTrigger data-testid="tab-account" value={SettingsTabsEnum.Account}>
            {t('tabs.account')}
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-organization"
            value={SettingsTabsEnum.Organization}
            disabled={orgDisabled}
            tooltip={orgTooltip}
          >
            {t('tabs.organization')}
          </TabsTrigger>
          <TabsTrigger
            data-testid="tab-billing"
            value={SettingsTabsEnum.Billing}
            disabled={billingDisabled}
            tooltip={billingTooltip}
          >
            {t('tabs.billing')}
          </TabsTrigger>
        </TabsList>
        {/* Radix Tabs only mounts the active panel, so the gated tabs never
            render. No need to repeat the disabled flags here. */}
        <TabsContent value={SettingsTabsEnum.Account}>
          <UserProfilePageLoader />
        </TabsContent>
        <TabsContent value={SettingsTabsEnum.Organization}>
          <LoadingAccountSettingsPage>
            <AccountSettingsPage />
          </LoadingAccountSettingsPage>
        </TabsContent>
        <TabsContent value={SettingsTabsEnum.Billing}>
          <SubscriptionLoader />
        </TabsContent>
      </Tabs>
    </div>
  );
};
